// PasswordResetConfirm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import { validatePasswordStrength, getPasswordRequirements } from './passwordStrength';

const PasswordResetConfirm = () => {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);

  const passwordRequirements = getPasswordRequirements();

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        await apiClient.get(`/api/password-reset-confirm/${uidb64}/${token}/`);
        setIsTokenValid(true);
      } catch (error) {
        setError('Invalid or expired reset link. Please request a new one.');
        setIsTokenValid(false);
      }
    };
    
    verifyToken();
  }, [uidb64, token]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'new_password') {
      setPasswordStrength(validatePasswordStrength(value));
    }
  };

  const getStrengthColor = (level) => {
    switch (level) {
      case 'strong': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'weak': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStrengthText = (level) => {
    switch (level) {
      case 'strong': return 'Strong';
      case 'medium': return 'Medium';
      case 'weak': return 'Weak';
      default: return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordStrength.isValid) {
      setError('Please use a stronger password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { data } = await apiClient.post('/api/password-reset-complete/', {
        uid: uidb64,
        token: token,
        new_password: formData.new_password
      });

      setMessage(data.message);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isTokenValid && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-red-500 text-center mb-4">
            {error}
          </div>
          <div className="text-center">
            <a href="/password-reset" className="text-blue-500 hover:underline">
              Request new reset link
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold text-center mb-6">Set New Password</h2>
        
        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
            {message} Redirecting to login...
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handlePasswordChange}
              onFocus={() => setShowRequirements(true)}
              onBlur={() => setTimeout(() => setShowRequirements(false), 200)}
              className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
              required
              minLength={8}
              placeholder="Enter new password"
            />
            
            {/* Password Strength Indicator */}
            {formData.new_password && (
              <div className="mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Password strength:</span>
                  <span className={`text-sm font-medium ${
                    passwordStrength.strengthLevel === 'strong' ? 'text-green-600' :
                    passwordStrength.strengthLevel === 'medium' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {getStrengthText(passwordStrength.strengthLevel)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${getStrengthColor(passwordStrength.strengthLevel)} transition-all duration-300`}
                    style={{ 
                      width: passwordStrength.strengthLevel === 'strong' ? '100%' : 
                             passwordStrength.strengthLevel === 'medium' ? '66%' : '33%' 
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Password Requirements */}
            {showRequirements && (
              <div className="mt-3 p-3 bg-gray-50 rounded border">
                <p className="text-sm font-medium mb-2">Password must contain:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {passwordRequirements.map((req, index) => (
                    <li 
                      key={index}
                      className={`flex items-center ${
                        index === 0 && formData.new_password.length >= 8 ? 'text-green-600' :
                        index === 1 && /[A-Z]/.test(formData.new_password) ? 'text-green-600' :
                        index === 2 && /[a-z]/.test(formData.new_password) ? 'text-green-600' :
                        index === 3 && /[0-9]/.test(formData.new_password) ? 'text-green-600' :
                        index === 4 && /[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password) ? 'text-green-600' : ''
                      }`}
                    >
                      <span className="mr-2">
                        {(
                          (index === 0 && formData.new_password.length >= 8) ||
                          (index === 1 && /[A-Z]/.test(formData.new_password)) ||
                          (index === 2 && /[a-z]/.test(formData.new_password)) ||
                          (index === 3 && /[0-9]/.test(formData.new_password)) ||
                          (index === 4 && /[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password))
                        ) ? '✓' : '○'}
                      </span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Confirm Password</label>
            <input
              type="password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handlePasswordChange}
              className={`w-full p-2 border rounded focus:outline-none ${
                formData.confirm_password && formData.new_password !== formData.confirm_password
                  ? 'border-red-500'
                  : 'focus:border-blue-500'
              }`}
              required
              minLength={8}
              placeholder="Confirm new password"
            />
            {formData.confirm_password && formData.new_password !== formData.confirm_password && (
              <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !passwordStrength.isValid || formData.new_password !== formData.confirm_password}
            className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <a href="/password-reset" className="text-blue-500 hover:underline text-sm">
            Need a new reset link?
          </a>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;